import React, { useState, useEffect } from 'react';
import { calculatePlanetaryPositions, calculateAscendant } from '../utils/ephemeris';

const ZODIAC_SIGNS = [
  { name: 'Aries', symbol: '♈', color: '#FF6B6B' },
  { name: 'Taurus', symbol: '♉', color: '#4ECDC4' },
  { name: 'Gemini', symbol: '♊', color: '#45B7D1' },
  { name: 'Cancer', symbol: '♋', color: '#96CEB4' },
  { name: 'Leo', symbol: '♌', color: '#FFEAA7' },
  { name: 'Virgo', symbol: '♍', color: '#DDA0DD' },
  { name: 'Libra', symbol: '♎', color: '#FFB6C1' },
  { name: 'Scorpio', symbol: '♏', color: '#8B008B' },
  { name: 'Sagittarius', symbol: '♐', color: '#FF69B4' },
  { name: 'Capricorn', symbol: '♑', color: '#708090' },
  { name: 'Aquarius', symbol: '♒', color: '#00CED1' },
  { name: 'Pisces', symbol: '♓', color: '#9370DB' }
];

// Major aspects with their angles and orbs (colors match astro-consolidated-app)
const MAJOR_ASPECTS = [
  { name: 'Conjunction', angle: 0, orb: 8, color: '#9370DB', style: 'solid' },   // Purple
  { name: 'Opposition', angle: 180, orb: 8, color: '#FFA500', style: 'solid' },  // Orange
  { name: 'Trine', angle: 120, orb: 8, color: '#0000FF', style: 'solid' },       // Blue
  { name: 'Square', angle: 90, orb: 8, color: '#FF0000', style: 'solid' },       // Red
  { name: 'Sextile', angle: 60, orb: 6, color: '#00FF00', style: 'dashed' }      // Green
];

// Helper function to get gradient based on zodiac element
const getZodiacGradient = (signName) => {
  const fireElements = ['Aries', 'Leo', 'Sagittarius'];
  const earthElements = ['Taurus', 'Virgo', 'Capricorn'];
  const airElements = ['Gemini', 'Libra', 'Aquarius'];
  const waterElements = ['Cancer', 'Scorpio', 'Pisces'];

  if (fireElements.includes(signName)) return 'url(#gradient-fire)';
  if (earthElements.includes(signName)) return 'url(#gradient-earth)';
  if (airElements.includes(signName)) return 'url(#gradient-air)';
  if (waterElements.includes(signName)) return 'url(#gradient-water)';
  return 'rgba(26, 0, 51, 0.1)'; // Fallback
};

const SimpleWheelFixed = ({
  natalDate,
  // Optional exact birth UTC instant (stored profiles). When provided it is
  // used directly, bypassing the wall-clock reconstruction from natalDate.
  natalDateUTC: natalDateUTCProp,
  transitDate,
  houseSystem,
  latitude,
  longitude,
  timezone,
  utcOffset,
  firstHouseReference,
  manualFirstHouseSign,
  natalPlanetFilter,
  transitPlanetFilter,
  onDataUpdate
}) => {
  const [hoveredPlanet, setHoveredPlanet] = useState(null);
  const centerX = 300;
  const centerY = 300;
  const houseNumberRadius = 295;  // Outermost ring for house numbers
  const outerRadius = 280;
  const transitRadius = 240;
  const natalRadius = 200;
  const innerRadius = 160;

  // Convert natal date to UTC for accurate calculations
  const year = natalDate.getFullYear();
  const month = natalDate.getMonth();
  const day = natalDate.getDate();
  const hours = natalDate.getHours();
  const minutes = natalDate.getMinutes();
  const seconds = natalDate.getSeconds();

  // Create UTC date by treating the displayed time as birth location time
  const natalDateUTC = natalDateUTCProp || new Date(Date.UTC(year, month, day, hours, minutes, seconds) - utcOffset * 60 * 60 * 1000);

  const allNatalPlanetsData = calculatePlanetaryPositions(natalDateUTC);
  const allTransitPlanetsData = calculatePlanetaryPositions(transitDate);

  // Calculate actual ascendant from birth location and time (using UTC)
  const ascendantLongitude = calculateAscendant(natalDateUTC, latitude, longitude);

  // Determine the first house longitude based on selected reference
  let firstHouseLongitude = ascendantLongitude; // Default to ascendant

  // List of zodiac sign names (lowercase for comparison)
  const signNames = ['aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo',
                     'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces'];
  const signNamesCapitalized = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
                                'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];

  if (firstHouseReference === 'sun') {
    const sunData = allNatalPlanetsData.find(p => p.name === 'Sun');
    if (sunData) firstHouseLongitude = sunData.longitude;
  } else if (firstHouseReference === 'moon') {
    const moonData = allNatalPlanetsData.find(p => p.name === 'Moon');
    if (moonData) firstHouseLongitude = moonData.longitude;
  } else if (firstHouseReference === 'manual') {
    const signIdx = signNamesCapitalized.indexOf(manualFirstHouseSign);
    firstHouseLongitude = signIdx * 30;
  } else if (signNames.includes(firstHouseReference.toLowerCase())) {
    const signIdx = signNames.indexOf(firstHouseReference.toLowerCase());
    firstHouseLongitude = signIdx * 30;
  }

  // House 1 is always at 270° in the visual representation
  const house1Position = 270;

  // Calculate which zodiac sign the first house falls in
  const signIndex = Math.floor(firstHouseLongitude / 30);

  // Convert angle for SVG
  const angleToSVG = (angle) => angle - 90;

  // Create path for zodiac segment
  const createZodiacPath = (index) => {
    const startAngle = angleToSVG(house1Position - index * 30);
    const endAngle = angleToSVG(house1Position - (index + 1) * 30);

    const startOuterX = centerX + outerRadius * Math.cos(startAngle * Math.PI / 180);
    const startOuterY = centerY + outerRadius * Math.sin(startAngle * Math.PI / 180);
    const endOuterX = centerX + outerRadius * Math.cos(endAngle * Math.PI / 180);
    const endOuterY = centerY + outerRadius * Math.sin(endAngle * Math.PI / 180);

    const startInnerX = centerX + transitRadius * Math.cos(startAngle * Math.PI / 180);
    const startInnerY = centerY + transitRadius * Math.sin(startAngle * Math.PI / 180);
    const endInnerX = centerX + transitRadius * Math.cos(endAngle * Math.PI / 180);
    const endInnerY = centerY + transitRadius * Math.sin(endAngle * Math.PI / 180);

    return `
      M ${startOuterX} ${startOuterY}
      A ${outerRadius} ${outerRadius} 0 0 0 ${endOuterX} ${endOuterY}
      L ${endInnerX} ${endInnerY}
      A ${transitRadius} ${transitRadius} 0 0 1 ${startInnerX} ${startInnerY}
      Z
    `;
  };

  // Convert ephemeris data to visual positions
  const convertToVisualPosition = (planetData, radius) => {
    const actualLongitude = planetData.longitude;
    const ascendantSignStartLongitude = Math.floor(firstHouseLongitude / 30) * 30;
    const visualAngle = (house1Position - (actualLongitude - ascendantSignStartLongitude) + 720) % 360;

    const svgAngle = angleToSVG(visualAngle);
    const x = centerX + radius * Math.cos(svgAngle * Math.PI / 180);
    const y = centerY + radius * Math.sin(svgAngle * Math.PI / 180);

    return {
      x,
      y,
      angle: visualAngle,
      zodiacSign: planetData.sign,
      degree: Math.floor(planetData.degreeInSign),
      minute: Math.floor((planetData.degreeInSign % 1) * 60),
      name: planetData.name,
      symbol: planetData.symbol,
      color: planetData.color,
      isRetrograde: planetData.isRetrograde
    };
  };

  // Calculate house cusps based on house system
  const getHouseCusps = () => {
    const cusps = [];
    for (let i = 0; i < 12; i++) {
      cusps.push((house1Position - i * 30 + 360) % 360);
    }
    return cusps;
  };

  // Calculate which house a planet is in based on its longitude
  const getHouseNumber = (planetLongitude) => {
    const planetSignIndex = Math.floor(planetLongitude / 30);
    const houseNumber = ((planetSignIndex - signIndex + 12) % 12) + 1;
    return houseNumber;
  };

  // Filter planets based on selection
  const filterPlanets = (planets, filter) => {
    if (filter === 'all') return planets;
    if (filter === 'none') return [];

    const innerPlanets = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars'];
    const outerPlanets = ['Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto'];
    const nodes = ['North Node', 'South Node'];

    if (filter === 'inner') {
      return planets.filter(p => innerPlanets.includes(p.name));
    } else if (filter === 'outer') {
      return planets.filter(p => outerPlanets.includes(p.name));
    } else if (filter === 'outer-nodes') {
      return planets.filter(p => outerPlanets.includes(p.name) || nodes.includes(p.name));
    } else if (filter === 'nodes') {
      return planets.filter(p => nodes.includes(p.name));
    } else {
      const planetName = filter.charAt(0).toUpperCase() + filter.slice(1);
      return planets.filter(p => p.name === planetName);
    }
  };

  // Check if two planets form an aspect
  const checkAspect = (angle1, angle2) => {
    let diff = Math.abs(angle1 - angle2);
    if (diff > 180) diff = 360 - diff;

    for (const aspect of MAJOR_ASPECTS) {
      const orb = Math.abs(diff - aspect.angle);
      if (orb <= aspect.orb) {
        return { ...aspect, actualOrb: orb };
      }
    }
    return null;
  };

  // Convert all planet data to visual positions
  const allNatalPositions = allNatalPlanetsData.map(planetData => ({
    ...convertToVisualPosition(planetData, natalRadius),
    type: 'natal',
    longitude: planetData.longitude,
    house: getHouseNumber(planetData.longitude)
  }));

  const allTransitPositions = allTransitPlanetsData.map(planetData => ({
    ...convertToVisualPosition(planetData, transitRadius - 15),
    type: 'transit',
    longitude: planetData.longitude,
    house: getHouseNumber(planetData.longitude)
  }));

  // Apply filters
  const natalPositions = filterPlanets(allNatalPositions, natalPlanetFilter);
  let transitPositions = filterPlanets(allTransitPositions, transitPlanetFilter);

  // Adjust overlapping transit planets for better visibility
  const minDistance = 30; // Minimum pixels between planet centers
  transitPositions = transitPositions.map((planet, i) => {
    let adjustedX = planet.x;
    let adjustedY = planet.y;

    // Check for overlaps with previous planets
    for (let j = 0; j < i; j++) {
      const other = transitPositions[j];
      const dx = adjustedX - other.x;
      const dy = adjustedY - other.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < minDistance) {
        // Move planet slightly outward from center to avoid overlap
        const angle = Math.atan2(adjustedY - centerY, adjustedX - centerX);
        adjustedX += Math.cos(angle) * 10;
        adjustedY += Math.sin(angle) * 10;
      }
    }

    return { ...planet, x: adjustedX, y: adjustedY };
  });

  // Calculate aspects between natal planets themselves
  const natalAspects = [];
  for (let i = 0; i < natalPositions.length; i++) {
    for (let j = i + 1; j < natalPositions.length; j++) {
      const natal1 = natalPositions[i];
      const natal2 = natalPositions[j];
      const aspect = checkAspect(natal1.angle, natal2.angle);
      if (aspect) {
        natalAspects.push({
          natal1,
          natal2,
          aspect,
          key: `${natal1.name}-${natal2.name}`
        });
      }
    }
  }

  // Calculate aspects between transit planets themselves
  const transitAspects = [];
  for (let i = 0; i < transitPositions.length; i++) {
    for (let j = i + 1; j < transitPositions.length; j++) {
      const transit1 = transitPositions[i];
      const transit2 = transitPositions[j];
      const aspect = checkAspect(transit1.angle, transit2.angle);
      if (aspect) {
        transitAspects.push({
          transit1,
          transit2,
          aspect,
          key: `${transit1.name}-${transit2.name}`
        });
      }
    }
  }

  const houseCusps = getHouseCusps();

  // Calculate first house reference data for display
  const firstHouseSignIndex = Math.floor(firstHouseLongitude / 30);
  const firstHouseDegreeInSign = firstHouseLongitude % 30;

  // Determine label based on reference type
  let firstHouseLabel = 'Ascendant';
  let firstHouseSymbol = 'ASC';
  if (firstHouseReference === 'sun') {
    firstHouseLabel = 'Sun (1st House)';
    firstHouseSymbol = '☉';
  } else if (firstHouseReference === 'moon') {
    firstHouseLabel = 'Moon (1st House)';
    firstHouseSymbol = '☽';
  } else if (firstHouseReference === 'manual') {
    firstHouseLabel = `${manualFirstHouseSign} (1st House)`;
    firstHouseSymbol = ZODIAC_SIGNS.find(z => z.name === manualFirstHouseSign)?.symbol || '♈';
  } else if (signNames.includes(firstHouseReference.toLowerCase())) {
    const signIdx = signNames.indexOf(firstHouseReference.toLowerCase());
    const signName = signNamesCapitalized[signIdx];
    firstHouseLabel = `${signName} (1st House)`;
    firstHouseSymbol = ZODIAC_SIGNS.find(z => z.name === signName)?.symbol || '♈';
  }

  const ascendantData = {
    name: firstHouseLabel,
    symbol: firstHouseSymbol,
    longitude: firstHouseLongitude,
    zodiacSign: ZODIAC_SIGNS[firstHouseSignIndex].name,
    degree: Math.floor(firstHouseDegreeInSign),
    minute: Math.floor((firstHouseDegreeInSign % 1) * 60)
  };

  // Update parent component with planetary data
  useEffect(() => {
    if (onDataUpdate) {
      onDataUpdate({
        natalPositions,
        transitPositions,
        natalAspects,
        transitAspects,
        ascendant: ascendantData
      });
    }
  }, [natalPositions, transitPositions, natalAspects, transitAspects, ascendantLongitude, onDataUpdate]);

  return (
    <svg viewBox="0 0 600 600" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style={{ background: 'radial-gradient(circle at center, rgba(26, 0, 51, 0.95) 0%, rgba(13, 0, 26, 0.98) 60%, #0d001a 100%)', border: '2px solid rgba(218, 165, 32, 0.4)', borderRadius: '8px', boxShadow: '0 0 40px rgba(138, 43, 226, 0.3), inset 0 0 100px rgba(138, 43, 226, 0.1)', maxWidth: '100%' }}>
      {/* Gradient Definitions for Zodiac Signs */}
      <defs>
        {/* Fire Signs: Aries, Leo, Sagittarius - Warm gradients */}
        <radialGradient id="gradient-fire" cx="50%" cy="50%">
          <stop offset="0%" stopColor="rgba(255, 107, 107, 0.15)" />
          <stop offset="100%" stopColor="rgba(139, 0, 0, 0.08)" />
        </radialGradient>

        {/* Earth Signs: Taurus, Virgo, Capricorn - Green/Brown gradients */}
        <radialGradient id="gradient-earth" cx="50%" cy="50%">
          <stop offset="0%" stopColor="rgba(78, 205, 196, 0.15)" />
          <stop offset="100%" stopColor="rgba(112, 128, 144, 0.08)" />
        </radialGradient>

        {/* Air Signs: Gemini, Libra, Aquarius - Blue gradients */}
        <radialGradient id="gradient-air" cx="50%" cy="50%">
          <stop offset="0%" stopColor="rgba(69, 183, 209, 0.15)" />
          <stop offset="100%" stopColor="rgba(0, 206, 209, 0.08)" />
        </radialGradient>

        {/* Water Signs: Cancer, Scorpio, Pisces - Teal/Purple gradients */}
        <radialGradient id="gradient-water" cx="50%" cy="50%">
          <stop offset="0%" stopColor="rgba(147, 112, 219, 0.15)" />
          <stop offset="100%" stopColor="rgba(139, 0, 139, 0.08)" />
        </radialGradient>
      </defs>

      {/* Outer circle for house numbers */}
      <circle
        cx={centerX}
        cy={centerY}
        r={houseNumberRadius}
        fill="none"
        stroke="rgba(218, 165, 32, 0.5)"
        strokeWidth="2"
      />

      {/* Zodiac Signs */}
      {Array.from({ length: 12 }, (_, positionIndex) => {
        const actualSignIndex = (signIndex + positionIndex) % 12;
        const actualSign = ZODIAC_SIGNS[actualSignIndex];
        const midAngle = house1Position - positionIndex * 30 - 15;

        return (
          <g key={`zodiac-position-${positionIndex}`}>
            <path
              d={createZodiacPath(positionIndex)}
              fill={getZodiacGradient(actualSign.name)}
              stroke="rgba(218, 165, 32, 0.3)"
              strokeWidth="1"
            />
            <text
              x={centerX + (outerRadius - 20) * Math.cos(angleToSVG(midAngle) * Math.PI / 180)}
              y={centerY + (outerRadius - 20) * Math.sin(angleToSVG(midAngle) * Math.PI / 180)}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="18"
              fill="#ffd700"
              fontWeight="bold"
              style={{ filter: 'drop-shadow(0 0 3px rgba(218, 165, 32, 0.8))' }}
            >
              {actualSign.symbol}
            </text>
          </g>
        );
      })}

      {/* House lines */}
      {houseCusps.map((cusp, i) => {
        const angle = angleToSVG(cusp);
        return (
          <g key={`house-${i}`}>
            <line
              x1={centerX + houseNumberRadius * Math.cos(angle * Math.PI / 180)}
              y1={centerY + houseNumberRadius * Math.sin(angle * Math.PI / 180)}
              x2={centerX + innerRadius * Math.cos(angle * Math.PI / 180)}
              y2={centerY + innerRadius * Math.sin(angle * Math.PI / 180)}
              stroke="rgba(218, 165, 32, 0.4)"
              strokeWidth={i === 0 || i === 3 || i === 6 || i === 9 ? "2" : "1"}
              strokeDasharray={i === 0 || i === 3 || i === 6 || i === 9 ? "none" : "2,2"}
            />
            {/* Add ASC label for House 1 */}
            {i === 0 && (
              <text
                x={centerX + (innerRadius - 15) * Math.cos(angle * Math.PI / 180)}
                y={centerY + (innerRadius - 15) * Math.sin(angle * Math.PI / 180)}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="10"
                fill="#ffd700"
                fontWeight="bold"
                style={{ filter: 'drop-shadow(0 0 4px rgba(218, 165, 32, 0.8))' }}
              >
                ASC
              </text>
            )}
            {/* Add MC label for House 10 */}
            {i === 9 && (
              <text
                x={centerX + (innerRadius - 15) * Math.cos(angle * Math.PI / 180)}
                y={centerY + (innerRadius - 15) * Math.sin(angle * Math.PI / 180)}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="10"
                fill="#9370DB"
                fontWeight="bold"
                style={{ filter: 'drop-shadow(0 0 4px rgba(147, 112, 219, 0.8))' }}
              >
                MC
              </text>
            )}
          </g>
        );
      })}

      {/* House numbers in outer ring */}
      {houseCusps.map((cusp, i) => {
        const nextCusp = houseCusps[(i + 1) % 12];
        let midAngle = (cusp + nextCusp) / 2;
        if (cusp < nextCusp) {
          midAngle = ((cusp + nextCusp + 360) / 2) % 360;
        }
        const midAngleSVG = angleToSVG(midAngle);

        return (
          <text
            key={`house-num-${i}`}
            x={centerX + ((houseNumberRadius + outerRadius) / 2) * Math.cos(midAngleSVG * Math.PI / 180)}
            y={centerY + ((houseNumberRadius + outerRadius) / 2) * Math.sin(midAngleSVG * Math.PI / 180)}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="12"
            fill={i === 0 ? "#ffd700" : i === 9 ? "#9370DB" : "rgba(218, 165, 32, 0.7)"}
            fontWeight="bold"
            style={{ filter: 'drop-shadow(0 0 2px rgba(0, 0, 0, 0.8))' }}
          >
            {i + 1}
          </text>
        );
      })}

      {/* Inner circle */}
      <circle
        cx={centerX}
        cy={centerY}
        r={innerRadius}
        fill="rgba(13, 0, 26, 0.6)"
        stroke="rgba(218, 165, 32, 0.6)"
        strokeWidth="2"
      />

      {/* Natal-to-Natal Aspect lines - drawn in the inner circle */}
      {natalAspects.map(({ natal1, natal2, aspect, key }) => {
        const aspectRadius = innerRadius - 20;
        const angle1SVG = angleToSVG(natal1.angle);
        const angle2SVG = angleToSVG(natal2.angle);
        const x1 = centerX + aspectRadius * Math.cos(angle1SVG * Math.PI / 180);
        const y1 = centerY + aspectRadius * Math.sin(angle1SVG * Math.PI / 180);
        const x2 = centerX + aspectRadius * Math.cos(angle2SVG * Math.PI / 180);
        const y2 = centerY + aspectRadius * Math.sin(angle2SVG * Math.PI / 180);

        return (
          <line
            key={`natal-aspect-${key}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={aspect.color}
            strokeWidth="1.5"
            strokeDasharray={aspect.style === 'dashed' ? '5,5' : 'none'}
            opacity="0.6"
          />
        );
      })}

      {/* Natal Planets */}
      {natalPositions.map((planet) => {
        const angleToCenter = Math.atan2(centerY - planet.y, centerX - planet.x);
        const labelOffset = 22;
        const labelX = planet.x + Math.cos(angleToCenter) * labelOffset;
        const labelY = planet.y + Math.sin(angleToCenter) * labelOffset;

        return (
        <g key={`natal-${planet.name}`}>
          <circle
            cx={planet.x}
            cy={planet.y}
            r="14"
            fill="#4a90e2"
            stroke="white"
            strokeWidth="2"
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHoveredPlanet(`natal-${planet.name}`)}
            onMouseLeave={() => setHoveredPlanet(null)}
          />
          <text
            x={planet.x}
            y={planet.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="14"
            fill="white"
            fontWeight="bold"
            pointerEvents="none"
          >
            {planet.symbol}
          </text>

          {/* Degree label */}
          <text
            x={labelX}
            y={labelY}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="9"
            fill="#4a90e2"
            fontWeight="bold"
            pointerEvents="none"
          >
            {planet.degree}°{planet.minute}'
          </text>

          {/* Tooltip */}
          {hoveredPlanet === `natal-${planet.name}` && (
            <g>
              <rect
                x={planet.x + 20}
                y={planet.y - 20}
                width="140"
                height="40"
                fill="white"
                stroke="#333"
                strokeWidth="1"
                rx="4"
              />
              <text
                x={planet.x + 90}
                y={planet.y - 5}
                textAnchor="middle"
                fontSize="12"
                fill="#333"
              >
                {planet.name} (Natal)
              </text>
              <text
                x={planet.x + 90}
                y={planet.y + 10}
                textAnchor="middle"
                fontSize="12"
                fill="#666"
              >
                {planet.zodiacSign} {planet.degree}° {planet.minute}'
              </text>
            </g>
          )}
        </g>
        );
      })}

      {/* Transit-to-Transit Aspect Lines */}
      {transitAspects.map(({ transit1, transit2, aspect, key }) => (
        <line
          key={`transit-aspect-${key}`}
          x1={transit1.x}
          y1={transit1.y}
          x2={transit2.x}
          y2={transit2.y}
          stroke={aspect.color}
          strokeWidth="1.5"
          strokeDasharray={aspect.style === 'dashed' ? '5,5' : 'none'}
          opacity="0.6"
        />
      ))}

      {/* Transit Planets */}
      {transitPositions.map((planet) => {
        const angleFromCenter = Math.atan2(planet.y - centerY, planet.x - centerX);
        const labelOffset = 22;
        const labelX = planet.x + Math.cos(angleFromCenter) * labelOffset;
        const labelY = planet.y + Math.sin(angleFromCenter) * labelOffset;

        return (
        <g key={`transit-${planet.name}`}>
          <circle
            cx={planet.x}
            cy={planet.y}
            r="14"
            fill="#e24a4a"
            stroke="white"
            strokeWidth="2"
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHoveredPlanet(`transit-${planet.name}`)}
            onMouseLeave={() => setHoveredPlanet(null)}
          />
          <text
            x={planet.x}
            y={planet.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="14"
            fill="white"
            fontWeight="bold"
            pointerEvents="none"
          >
            {planet.symbol}
          </text>

          {/* Degree label */}
          <text
            x={labelX}
            y={labelY}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="9"
            fill="#e24a4a"
            fontWeight="bold"
            pointerEvents="none"
          >
            {planet.degree}°{planet.minute}'
          </text>

          {/* Tooltip */}
          {hoveredPlanet === `transit-${planet.name}` && (
            <g>
              <rect
                x={planet.x + 20}
                y={planet.y - 20}
                width="140"
                height="40"
                fill="white"
                stroke="#333"
                strokeWidth="1"
                rx="4"
              />
              <text
                x={planet.x + 90}
                y={planet.y - 5}
                textAnchor="middle"
                fontSize="12"
                fill="#333"
              >
                {planet.name} (Transit)
              </text>
              <text
                x={planet.x + 90}
                y={planet.y + 10}
                textAnchor="middle"
                fontSize="12"
                fill="#666"
              >
                {planet.zodiacSign} {planet.degree}° {planet.minute}'
              </text>
            </g>
          )}
        </g>
        );
      })}

      {/* Center info */}
      <text
        x={centerX}
        y={centerY - 10}
        textAnchor="middle"
        fontSize="11"
        fill="#666"
      >
        {houseSystem.toUpperCase()}
      </text>
      <text
        x={centerX}
        y={centerY + 5}
        textAnchor="middle"
        fontSize="10"
        fill="#999"
      >
        {natalDate.toLocaleDateString()}
      </text>
    </svg>
  );
};

export default SimpleWheelFixed;
